import {
  getStories,
  getStoryBySlug,
  useStoryblokState,
} from '@cinch-nx/data-storyblok'
import { GuidesHubFeature } from '@cinch-nx/landing-feature-guides-hub'
import type {
  ArticlePreview,
  ArticleStoryContent,
  GuidesHubPageStoryContent,
} from '@cinch-nx/landing-ui-articles'
import { Head } from '@cinch-nx/landing-ui-shared'

import { tagError, setTag, traceFunction } from '@core/datadog-server'

import type { InferNextProps } from '../../types'

const instrumentedGetStoryBySlug = traceFunction(
  'getStoryBySlug',
  getStoryBySlug,
)

const instrumentedGetStories = traceFunction('getStories', getStories)

type RecentArticleStories = {
  [key: string]: ArticlePreview[]
}

function GuidesHub({
  guidesHubStory,
  recentArticleStories,
  preview,
}: InferNextProps<typeof getStaticProps>) {
  const { content } = useStoryblokState({
    story: guidesHubStory,
    enableBridge: preview,
  })
  return (
    <>
      <Head
        fallbackTitle="Cars Without The Faff - cinch"
        fallbackDescription="Welcome to cinch, the faff free way to buy & sell cars. We have thousands of used cars, ready to be sold to you."
      />

      <GuidesHubFeature
        content={content}
        recentArticleStories={recentArticleStories}
      />
    </>
  )
}

export const getStaticProps = async ({ preview = false }) => {
  try {
    setTag('path', '/guides')
    const version = preview ? 'draft' : 'published'
    setTag('version', version)
    const guidesHubStory =
      await instrumentedGetStoryBySlug<GuidesHubPageStoryContent>(
        'articles/hubs/guides',
        {
          version: preview ? 'draft' : 'published',
        },
      )
    const guidesHubSubHubs = [
      'articles/guides/choosing-a-car',
      'articles/guides/car-finance',
      'articles/guides/car-maintenance',
      'articles/guides/ask-the-experts',
      'articles/guides/electric-cars',
      'articles/guides/new-drivers',
    ]
    const NUMBER_IN_HUB = 4

    const rawArticles = guidesHubSubHubs.map(async (subject) => {
      const first4Articles = instrumentedGetStories<ArticleStoryContent>({
        starts_with: subject,
        version,
        per_page: NUMBER_IN_HUB,
        sort_by: 'published_at:desc',
      }).then((res) => res.stories)

      return first4Articles
    })

    const results = await Promise.all(rawArticles)

    const recentArticleStories: RecentArticleStories = results
      .flat()
      .reduce((acc: RecentArticleStories, story) => {
        // get the guide type from the full slug e.g 'articles/guides/choosing-a-car/some-article
        const type = story.full_slug.split('/')[2]

        if (!acc[type]) acc[type] = []

        // We only want the latest 4 stories for each guide type. (stories is already pre-sorted by first_published_at)
        if (acc[type].length >= 4) return acc

        const res: ArticlePreview = {
          _uid: story.content._uid,
          content: {
            component: story.content.component,
            title: story.content.title,
            image: story.content.image,
            _uid: story.content._uid,
          },
          first_published_at: story.published_at,
          full_slug: story.full_slug,
          slug: story.slug,
        }

        acc[type].push(res)

        return acc
      }, {})

    setTag('recentArticleStories', recentArticleStories.length)

    return {
      props: {
        guidesHubStory,
        recentArticleStories,
        preview,
        layoutProps: {
          showBorderRadius: true,
        },
      },

      revalidate: 60,
    }
  } catch (error) {
    console.log(error)
    if (typeof error === 'string' || error instanceof Error) {
      tagError(error)
    }
    setTag('response.statusCode', 404)

    return { notFound: true, revalidate: 60 } as const
  }
}

export default GuidesHub
